import * as Sentry from "@sentry/vue";
import posthog from "posthog-js";

import type { PostHogConfig } from "posthog-js";
import type { AnalyticsProvider } from "../types";

import {
  AnalyticsProviderApiKeyMissingError,
  AnalyticsProviderInitializationError,
} from "../exceptions";

export interface PostHogProviderConfig {
  apiKey?: string;
  options?: Partial<PostHogConfig>;
}

export class PostHogAnalyticsProvider implements AnalyticsProvider {
  constructor(private readonly config: PostHogProviderConfig = {}) {}

  init() {
    const apiKey = this.config.apiKey || import.meta.env.VITE_POSTHOG_API_KEY;

    if (!apiKey) {
      console.error(new AnalyticsProviderApiKeyMissingError(PostHogAnalyticsProvider.name));
      return false;
    }

    try {
      posthog.init(apiKey, {
        api_host: "https://us.i.posthog.com",
        defaults: "2025-11-30",
        person_profiles: "identified_only",
        session_recording: {
          recordCrossOriginIframes: true,
        },
        ...this.config.options,
      });
      return true;
    } catch (error) {
      Sentry.captureException(
        new AnalyticsProviderInitializationError(PostHogAnalyticsProvider.name, error as Error)
      );
      return false;
    }
  }

  trackEvent(eventName: string, properties?: Record<string, unknown>) {
    posthog.capture(eventName, properties);
  }

  trackException(error: Error, properties?: Record<string, unknown>) {
    posthog.captureException(error, properties);
  }

  onUserChange<TUserData extends { id: string | number } | null>(userData: TUserData | null) {
    if (!userData) {
      posthog.reset();
      return;
    }

    posthog.identify(String(userData.id), userData);
  }

  onUserPropertiesChange<TData extends Record<string, unknown>>(properties: TData) {
    posthog.setPersonProperties(properties);
  }
}
